// src/pages/Dashboard.tsx
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { DashboardSection } from '../components/DashboardSection.tsx'
import { TickersTable } from '../components/TickersTable.tsx'
import { CategoriesSunburstChart } from '../components/chart/CategoriesSunburstChart.tsx'
import { CategoriesTreeMap } from '../components/chart/CategoriesTreeMap.tsx'
import { useCategories } from '../hooks/useCategories.ts'
import { useDashboardChartPreferences } from '../hooks/useDashboardChartPreferences.ts'
import { useCategoriesTree } from '../hooks/useCategoriesTree.ts'
import { useCurrentUser } from '../hooks/useCurrentUser.ts'
import { useQuotes } from '../hooks/useQuotes.ts'
import { useTickers } from '../hooks/useTickers.ts'
import { CategoriesPage } from './Categories.tsx'
import styles from './Dashboard.module.css'

type SectionKey = 'charts' | 'categories' | 'tickers'

export function Dashboard() {
  const { userId } = useCurrentUser()
  const {
    data: categories = [],
    isLoading: isLoadingCategories,
    isError: isErrorCategories,
    error: categoriesError,
  } = useCategories(userId)
  const {
    data: tickers = [],
    isLoading: isLoadingTickers,
    isError: isErrorTickers,
    error: tickersError,
  } = useTickers(userId)
  const tickerSymbols = useMemo(() => tickers.map((item) => item.ticker), [tickers])
  const {
    data: quotesData,
    isFetching: isFetchingQuotes,
    isError: isErrorQuotes,
    error: quotesError,
  } = useQuotes(tickerSymbols)
  const [openSections, setOpenSections] = useState<Record<SectionKey, boolean>>({
    charts: true,
    categories: true,
    tickers: true,
  })
  const { chartMode, chartDepthByMode, setChartMode, setChartDepth } = useDashboardChartPreferences()

  const isLoadingCharts = isLoadingCategories || isLoadingTickers
  const hasChartData = categories.length > 0 || tickers.length > 0

  const chartError =
    categoriesError instanceof Error
      ? categoriesError.message
      : tickersError instanceof Error
        ? tickersError.message
        : quotesError instanceof Error
          ? quotesError.message
          : 'Erro desconhecido'

  const chartTree = useCategoriesTree({
    categories,
    tickers,
    quotesBySymbol: quotesData?.bySymbol ?? {},
  })

  function toggleSection(section: SectionKey) {
    setOpenSections((current) => ({
      ...current,
      [section]: !current[section],
    }))
  }

  return (
    <div className="page">
      <header className="header">
        <div className="header-title-row">
          <h1>Dashboard</h1>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Link className="btn btn--ghost btn--sm" to="/auth">
              Auth
            </Link>
            <Link className="btn btn--ghost btn--sm" to="/categorias">
              Categorias
            </Link>
            <Link className="btn btn--ghost btn--sm" to="/importar-carteira">
              Importar carteira
            </Link>
          </div>
        </div>
        <p className="subtitle">Fundos de Investimento Imobiliário · user #{userId}</p>
      </header>

      <main className="main">
        
        <DashboardSection
          title="Charts"
          isOpen={openSections.charts}
          onToggle={() => toggleSection('charts')}
          contentId="dashboard-charts-section"
          contentClassName={styles.chartContent}
          headerActions={
            openSections.charts ? (
              <div className={styles.chartHeaderActions}>
                <div className={styles.chartModeToggle} aria-label="Selecionar tipo de chart">
                  <button
                    type="button"
                    className={`${styles.chartModeButton} ${chartMode === 'sun' ? styles.chartModeButtonActive : ''}`}
                    onClick={() => setChartMode('sun')}
                  >
                    sun
                  </button>
                  <button
                    type="button"
                    className={`${styles.chartModeButton} ${chartMode === 'tree' ? styles.chartModeButtonActive : ''}`}
                    onClick={() => setChartMode('tree')}
                  >
                    tree
                  </button>
                  <label className={styles.chartDepthControl}>
                    <span className={styles.chartDepthLabel}>depth</span>
                    <select
                      className={styles.chartDepthSelect}
                      value={chartDepthByMode[chartMode]}
                      onChange={(event) => {
                        setChartDepth(chartMode, Number(event.target.value))
                      }}
                      aria-label={`Definir profundidade do chart ${chartMode}`}
                    >
                      {[1, 2, 3, 4, 5, 6].map((depth) => (
                        <option key={depth} value={depth}>
                          {depth}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </div>
            ) : null
          }
        >
          {isLoadingCharts ? <div className="status">Carregando dados do chart...</div> : null}

          {!isLoadingCharts && (isErrorCategories || isErrorTickers || isErrorQuotes) ? (
            <div className="status status--error">Erro: {chartError}</div>
          ) : null}

          {!isLoadingCharts && !isErrorCategories && !isErrorTickers && !isErrorQuotes && !hasChartData ? (
            <div className="status">Nenhum dado para exibir no chart.</div>
          ) : null}

          {!isLoadingCharts && !isErrorCategories && !isErrorTickers && !isErrorQuotes && hasChartData ? (
            <div className={styles.chartArea}>
              {isFetchingQuotes ? <div className={styles.chartStatus}>Atualizando cotações...</div> : null}

              {chartMode === 'sun' ? (
                <CategoriesSunburstChart
                  tree={chartTree.tree}
                  tickersByCategory={chartTree.tickersByCategory}
                  leafDepth={chartDepthByMode.sun}
                />
              ) : (
                <CategoriesTreeMap
                  tree={chartTree.tree}
                  tickersByCategory={chartTree.tickersByCategory}
                  leafDepth={chartDepthByMode.tree}
                />
              )}
            </div>
          ) : null}
        </DashboardSection>

        <DashboardSection
          title="Categorias"
          isOpen={openSections.categories}
          onToggle={() => toggleSection('categories')}
          contentId="dashboard-categories-section"
          contentClassName={styles.fullBleedContent}
        >
          <CategoriesPage />
        </DashboardSection>

        <DashboardSection
          title="Tickers"
          isOpen={openSections.tickers}
          onToggle={() => toggleSection('tickers')}
          contentId="dashboard-tickers-section"
        >
          <TickersTable userId={userId} />
        </DashboardSection>
      </main>
    </div>
  )
}
